import { useLayoutEffect, useState } from "react";

import { isHostElementLaidOut } from "./maplibre-map.utils";

/**
 * Latches true once the map host has a non-zero box. MapLibre WebGL init in a 0×0 (or still-animating
 * drawer) container often never creates `painter`, and the later `remove` then throws on `destroy`.
 * Once the map has mounted we keep the latch even if the box later collapses, so a close animation
 * does not unmount the canvas mid-teardown.
 *
 * Takes the host element (callback ref), not a RefObject: if the node is missing on the first layout
 * pass the effect must run again when it appears, or the map never mounts.
 */
export const useMapLibreHostHasSize = (
  hostElement: HTMLElement | null,
): boolean => {
  const [hasSize, setHasSize] = useState(false);

  useLayoutEffect(() => {
    if (hasSize || !hostElement) {
      return;
    }

    const syncHasSize = (): void => {
      if (isHostElementLaidOut(hostElement)) {
        setHasSize(true);
      }
    };

    syncHasSize();
    const observer = new ResizeObserver(syncHasSize);
    observer.observe(hostElement);

    return () => {
      observer.disconnect();
    };
  }, [hasSize, hostElement]);

  return hasSize;
};
